import dayjs from 'dayjs';

/** Indian-format money: 12,34,567.89 */
export function money(n: any, decimals = 2): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.00';
  const neg = v < 0;
  const [intPart, decPart] = Math.abs(v).toFixed(decimals).split('.');
  let last3 = intPart.slice(-3);
  const rest = intPart.slice(0, -3);
  if (rest) last3 = `,${last3}`;
  const formatted = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + last3;
  return `${neg ? '-' : ''}${formatted}${decPart ? `.${decPart}` : ''}`;
}

export const rupees = (n: any, decimals = 2) => `₹${money(n, decimals)}`;

/** Money with the Dr/Cr suffix accountants expect. */
export function signed(n: any, decimals = 2): string {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.005) return '—';
  return `${money(Math.abs(v), decimals)} ${v >= 0 ? 'Dr' : 'Cr'}`;
}

/** Compact figures for dashboard cards: 12.4L, 1.2Cr */
export function compact(n: any): string {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return `${sign}${abs.toFixed(0)}`;
}

export function qty(n: any, decimals = 3): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  // Trim trailing zeros but keep at least one decimal place when fractional
  const fixed = v.toFixed(decimals);
  return fixed.replace(/\.?0+$/, '') || '0';
}

export const percent = (n: any, decimals = 2) => `${(Number(n) || 0).toFixed(decimals)}%`;

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------
export const ISO = 'YYYY-MM-DD';
export const today = () => dayjs().format(ISO);

/**
 * A stamp with a time but no zone ("2026-09-14 16:07:12") comes from the
 * text timestamp columns carried over from SQLite, which the server always
 * writes in UTC (datetime('now'), toISOString()). Read it as UTC so it shows
 * in the viewer's time; everything else (ISO with a zone, plain dates) is
 * read as it is (including a form's local "YYYY-MM-DDTHH:mm").
 */
const ZONELESS_STAMP = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
export function stamp(v: any) {
  return dayjs(typeof v === 'string' && ZONELESS_STAMP.test(v) ? `${v.replace(' ', 'T')}Z` : v);
}

export function date(v: any, fmt = 'DD MMM YYYY'): string {
  if (!v) return '—';
  const d = stamp(v);
  return d.isValid() ? d.format(fmt) : String(v);
}

export const shortDate = (v: any) => date(v, 'DD/MM/YY');
export const dateTime = (v: any) => date(v, 'DD MMM YYYY, HH:mm');

export function relative(v: any): string {
  if (!v) return '—';
  const d = stamp(v);
  if (!d.isValid()) return String(v);
  const mins = dayjs().diff(d, 'minute');
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.format('DD MMM YYYY');
}

/** Financial year containing a date, Indian convention. */
export function financialYearOf(d: any = today()) {
  const x = dayjs(d);
  const startYear = x.month() >= 3 ? x.year() : x.year() - 1;
  return {
    name: `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`,
    start: `${startYear}-04-01`,
    end: `${startYear + 1}-03-31`,
  };
}

/** Common date-range presets for report toolbars. */
export function datePresets(fyStart?: string, fyEnd?: string) {
  const t = dayjs();
  const fy = fyStart && fyEnd ? { start: fyStart, end: fyEnd } : financialYearOf();
  return [
    { key: 'today', label: 'Today', from: t.format(ISO), to: t.format(ISO) },
    { key: 'yesterday', label: 'Yesterday', from: t.subtract(1, 'day').format(ISO), to: t.subtract(1, 'day').format(ISO) },
    { key: 'this_week', label: 'This Week', from: t.startOf('week').format(ISO), to: t.format(ISO) },
    { key: 'this_month', label: 'This Month', from: t.startOf('month').format(ISO), to: t.endOf('month').format(ISO) },
    { key: 'last_month', label: 'Last Month', from: t.subtract(1, 'month').startOf('month').format(ISO), to: t.subtract(1, 'month').endOf('month').format(ISO) },
    { key: 'quarter', label: 'This Quarter', from: t.startOf('month').subtract(t.month() % 3, 'month').format(ISO), to: t.format(ISO) },
    { key: 'fy', label: 'Financial Year', from: fy.start, to: fy.end },
  ];
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------
export const VOUCHER_LABELS: Record<string, string> = {
  SI: 'Sales Invoice', SR: 'Sales Return', CN: 'Credit Note', DC: 'Delivery Challan',
  SQ: 'Quotation', SO: 'Sales Order',
  PI: 'Purchase Invoice', PR: 'Purchase Return', DN: 'Debit Note', GRN: 'Goods Receipt',
  PQ: 'Purchase Quotation', PO: 'Purchase Order',
  RC: 'Receipt', PY: 'Payment', CT: 'Contra', JV: 'Journal',
  ST: 'Stock Transfer', SA: 'Stock Adjustment', SJ: 'Stock Journal',
  MI: 'Material Issue', PRD: 'Production',
  DEP: 'Depreciation', OB: 'Opening Balance', YEC: 'Year End',
};

export const voucherLabel = (t: string) => VOUCHER_LABELS[t] || t;

export const STATUS_TONE: Record<string, string> = {
  POSTED: 'positive', CANCELLED: 'critical', REVERSED: 'caution',
  DRAFT: 'neutral', SUPERSEDED: 'neutral', PENDING: 'caution',
  PARTIAL: 'caution', COMPLETED: 'positive', SETTLED: 'positive', OPEN: 'brand',
  MATCHED: 'positive', PARTIAL_MATCH: 'caution', MISSING_IN_BOOKS: 'critical',
  MISSING_IN_2B: 'critical', GST_DIFFERENCE: 'critical', GENERATED: 'positive',
  FAILED: 'critical', ACTIVE: 'positive', ERROR: 'critical', WARNING: 'caution',
  INFO: 'brand', RESOLVED: 'positive',
};

export const statusTone = (s: string) => STATUS_TONE[s] || 'neutral';

/** Turn a snake_case key into a readable label. */
/*
 * The abbreviations that are read as letters and must stay in capitals. A
 * word not on this list is a word, and a word in a database column is not
 * shouted at the person reading it.
 */
const KEPT_IN_CAPITALS = new Set([
  'GST', 'IGST', 'CGST', 'SGST', 'UTGST', 'GSTIN', 'GSTR', 'HSN', 'SAC', 'ITC', 'RCM',
  'TDS', 'TCS', 'PAN', 'MSME', 'UQC', 'MRP', 'EWB', 'IRN', 'QR', 'UPI', 'NEFT', 'RTGS',
  'IMPS', 'LR', 'FIFO', 'FEFO', 'SKU', 'KPI', 'KRA', 'TA', 'DA', 'OT', 'ID', 'ERP', 'HR',
  'SEZ', 'EOU', 'LUT', 'B2B', 'B2C', 'CIF', 'FOB', 'EMI', 'PF', 'ESI', 'PT', 'CTC',
  'PO', 'GRN', 'DC', 'SI', 'PI', 'SR', 'PR', 'CN', 'DN', 'JV', 'RC', 'PY', 'CT', 'ST',
  'SA', 'SJ', 'MI', 'PRD', 'OB', 'YEC', 'DEP', 'SQ', 'SO', 'PQ', 'BOM', 'FG', 'RM', 'WIP',
]);

/**
 * Turn a stored code into something a person reads.
 *
 * UNREGISTERED becomes "Unregistered", NOT_STARTED becomes "Not started",
 * weekly_off becomes "Weekly off". It used to capitalise the first letter of
 * each word and leave the rest alone, so anything stored in capitals - which
 * is most codes - was shouted at the reader on every screen that showed it.
 */
export const humanise = (s: string) => String(s || '')
  .replace(/[_.]/g, ' ')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .map((word, i) => {
    const bare = word.replace(/[^A-Za-z0-9]/g, '');
    if (KEPT_IN_CAPITALS.has(bare.toUpperCase())) return word.toUpperCase();
    const lower = word.toLowerCase();
    // Only the first word takes a capital; the rest read as a sentence.
    return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
  })
  .join(' ');

export function initials(name: string) {
  return String(name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join('').toUpperCase();
}
